import { Css, DnDGridItemHandle, IconButton, Tag, useDnDGridItem, useModal } from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { observer } from "mobx-react";
import { useRef } from "react";
import { ConfigureBidGroupsStepQuery } from "src/routes/bids/plan-based-bid-package/components/2.ConfigureBidGroupsStep.generated";
import { AddOptionModal } from "src/routes/bids/plan-based-bid-package/components/AddOptionModal";
import { BidPlanDimensionRows } from "src/routes/bids/plan-based-bid-package/components/BidPlanDimensionRows";
import {
  PlanBasedBidPackageBidPlanGroup,
  PlanBasedBidPackageFormState,
} from "src/routes/bids/plan-based-bid-package/utils";
import { pluralize } from "src/utils";
import { optionGroupId } from "./OptionsSection";

type OptionCardProps = {
  formState: ObjectState<PlanBasedBidPackageFormState>;
  group: ObjectState<PlanBasedBidPackageBidPlanGroup>;
  dimensionResults: ConfigureBidGroupsStepQuery["potentialBidPlanDimensions"];
  potentialBidPlanOptions: ConfigureBidGroupsStepQuery["bidPackage"]["potentialBidPlanOptions"];
};

export const OptionCard = observer(function OptionCard(props: OptionCardProps) {
  const { formState, group, dimensionResults, potentialBidPlanOptions } = props;
  const { openModal } = useModal();
  const globalOptionId = group.globalOptionId.value;
  const option = potentialBidPlanOptions.find((o) => o.globalOption.id === globalOptionId);
  const planNames =
    option?.readyPlanOptions
      .map((rpo) => rpo.readyPlan)
      .uniqueByKey("id")
      .map((rp) => rp.name) ?? [];
  const itemRef = useRef(null);
  const { dragHandleProps, dragItemProps } = useDnDGridItem({ id: optionGroupId(group.value)!, itemRef });

  function openEditModal() {
    openModal({
      content: (
        <AddOptionModal
          formState={formState}
          dimensionResults={dimensionResults}
          potentialBidPlanOptions={potentialBidPlanOptions}
          editingGroup={group}
        />
      ),
    });
  }

  function removeOption() {
    const next = (formState.bidPlanGroups.value ?? []).filter((g) => g.globalOptionId !== globalOptionId);
    formState.bidPlanGroups.set(next);
  }

  return (
    <div css={Css.df.fdc.gap2.bt.bcGray200.pt3.$} ref={itemRef} {...dragItemProps}>
      <div css={Css.df.aic.jcsb.$}>
        <div css={Css.df.aic.gap1.$}>
          <div css={Css.lg.$}>{option?.globalOption.name ?? "Option"}</div>
          {planNames.nonEmpty && (
            <div css={Css.mya.$}>
              <Tag type="info" text={`${planNames.length} ${pluralize(planNames.length, "Product Offering")}`} />
            </div>
          )}
        </div>
        <div css={Css.df.aic.gap1.$}>
          <IconButton icon="pencil" onClick={openEditModal} />
          <IconButton icon="trash" onClick={removeOption} />
          <DnDGridItemHandle icon="drag" dragHandleProps={dragHandleProps} />
        </div>
      </div>
      {planNames.nonEmpty && <div>{planNames.join(", ")}</div>}
      <BidPlanDimensionRows group={group} dimensionResults={dimensionResults} />
    </div>
  );
});
